import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AcademySummary,
  AuthorPlanInfo,
  ActivationResult,
  Author,
  Banner,
  BannerClicker,
  CourseBanners,
  CourseDetail,
  CourseRow,
  DevLink,
  EnrollmentRow,
  Funnel,
  FunnelBoard,
  Impersonator,
  InviteResult,
  LessonDetail,
  MeResponse,
  PromoRow,
  PublicAcademy,
  PublicCourse,
  SectionWithLessons,
  SlugCheckResult,
  StudentProgress,
  StructureInput,
  StudentCourse,
  StudentLesson,
} from '../../shared/api';
import type { BannerInput } from '../../shared/banners';
import type { FunnelInput } from '../../shared/funnel';
import type { LessonBlock } from '../../shared/lessonBlocks';
import type { PromoInput } from '../../shared/validation';
import { api, isApiError } from './api';
import { hardNavigate } from './navigation';
import { platformPaths } from './urls';

export const keys = {
  me: ['me'] as const,
  plan: ['plan'] as const,
  academies: ['academies'] as const,
  courses: (academyId: string) => ['courses', academyId] as const,
  course: (courseId: string) => ['course', courseId] as const,
  lesson: (lessonId: string) => ['lesson', lessonId] as const,
  students: (academyId: string) => ['students', academyId] as const,
  studentProgress: (enrollmentId: string) => ['student-progress', enrollmentId] as const,
  promos: (academyId: string) => ['promos', academyId] as const,
  banners: (academyId: string) => ['banners', academyId] as const,
  courseBanners: (courseId: string) => ['course-banners', courseId] as const,
  bannerClicks: (bannerId: string) => ['banner-clicks', bannerId] as const,
  funnels: (academyId: string) => ['funnels', academyId] as const,
  funnelBoard: (funnelId: string) => ['funnel-board', funnelId] as const,
  slug: (slug: string, exclude?: string) => ['slug', slug, exclude ?? ''] as const,
  publicAcademy: (slug: string) => ['public-academy', slug] as const,
  publicCourse: (academy: string, course: string) => ['public-course', academy, course] as const,
  studentCourse: (academy: string, course: string) => ['student-course', academy, course] as const,
  studentLesson: (lessonId: string) => ['student-lesson', lessonId] as const,
};

/** Refreshes everything that shows course counts or lists after a content change. */
function invalidateAcademy(qc: QueryClient, academyId: string): void {
  void qc.invalidateQueries({ queryKey: keys.academies });
  void qc.invalidateQueries({ queryKey: keys.courses(academyId) });
  // Plan usage counts academies, courses and students.
  void qc.invalidateQueries({ queryKey: keys.plan });
}

// ---------- author session

/** The signed-in author; `impersonator` is set while an administrator acts as them. */
export interface Me extends Author {
  impersonator: Impersonator | null;
}

const asMe = (user: Author): Me => ({ ...user, impersonator: null });

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async (): Promise<Me | null> => {
      try {
        const data = await api.get<MeResponse>('/auth/me');
        return { ...data.user, impersonator: data.impersonator ?? null };
      } catch (error) {
        if (isApiError(error, 'unauthorized')) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export interface RegisterPayload {
  name: string;
  email: string;
  phone: string;
  password: string;
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegisterPayload) => api.post<{ user: Author } & DevLink>('/auth/register', payload),
    onSuccess: (data) => qc.setQueryData(keys.me, asMe(data.user)),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; password: string; remember: boolean }) => api.post<{ user: Author }>('/auth/login', payload),
    onSuccess: (data) => {
      qc.clear();
      qc.setQueryData(keys.me, asMe(data.user));
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => api.post<{ sent: boolean } & DevLink>('/auth/forgot', { email }) });
}

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { token: string; password: string }) => api.post<{ user: Author }>('/auth/reset', payload),
    onSuccess: (data) => qc.setQueryData(keys.me, asMe(data.user)),
  });
}

/** Leaves «Войти как автор»: back to the platform admin, or to login if the admin session expired. */
export function useEndImpersonation() {
  return useMutation({
    mutationFn: () => api.post<{ restored: boolean }>('/auth/impersonation/end'),
    onSuccess: (data) => hardNavigate(data.restored ? platformPaths.authors : '/login'),
  });
}

// ---------- plan

/** The author's plan and usage; a failure just hides the plan block. */
export function usePlanInfo() {
  return useQuery({ queryKey: keys.plan, queryFn: () => api.get<AuthorPlanInfo>('/plan'), retry: false, staleTime: 60_000 });
}

// ---------- academies

export function useAcademies(enabled = true) {
  return useQuery({ queryKey: keys.academies, queryFn: () => api.get<AcademySummary[]>('/academies'), enabled });
}

export function useSlugCheck(slug: string, exclude?: string) {
  return useQuery({
    queryKey: keys.slug(slug, exclude),
    queryFn: () => api.get<SlugCheckResult>(`/academies/slug-check?slug=${encodeURIComponent(slug)}${exclude ? `&exclude=${exclude}` : ''}`),
    enabled: slug.length > 0,
    staleTime: 10_000,
  });
}

export function useCreateAcademy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; slug: string }) => api.post<AcademySummary>('/academies', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.academies }),
  });
}

export function useUpdateAcademy(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; slug: string }) => api.patch<AcademySummary>(`/academies/${academyId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.academies }),
  });
}

/** Everything of the academy is gone; the plan usage and the sidebar list change. */
export function useDeleteAcademy(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/academies/${academyId}`),
    onSuccess: () => {
      // Dropped from the cached list at once, so /admin does not send the author back to it.
      qc.setQueryData<AcademySummary[]>(keys.academies, (list) => list?.filter((a) => a.id !== academyId));
      qc.removeQueries({ queryKey: keys.courses(academyId) });
      qc.removeQueries({ queryKey: keys.students(academyId) });
      qc.removeQueries({ queryKey: keys.promos(academyId) });
      void qc.invalidateQueries({ queryKey: keys.academies });
      void qc.invalidateQueries({ queryKey: keys.plan });
    },
  });
}

// ---------- banners

export function useBanners(academyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.banners(academyId ?? ''),
    queryFn: () => api.get<Banner[]>(`/academies/${academyId}/banners`),
    enabled: Boolean(academyId) && enabled,
  });
}

export function useBannerClicks(academyId: string, bannerId: string) {
  return useQuery({ queryKey: keys.bannerClicks(bannerId), queryFn: () => api.get<BannerClicker[]>(`/academies/${academyId}/banners/${bannerId}/clicks`) });
}

export function useFunnels(academyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.funnels(academyId ?? ''),
    queryFn: () => api.get<Funnel[]>(`/academies/${academyId}/funnels`),
    enabled: Boolean(academyId) && enabled,
  });
}

/** The funnel's students already placed in its stages by the server. */
export function useFunnelBoard(academyId: string, funnelId: string | undefined) {
  return useQuery({
    queryKey: keys.funnelBoard(funnelId ?? ''),
    queryFn: () => api.get<FunnelBoard>(`/academies/${academyId}/funnels/${funnelId}/board`),
    enabled: Boolean(funnelId),
  });
}

/** Creates a funnel, or replaces one when `id` is given; boards are placed anew. */
export function useSaveFunnel(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: FunnelInput }) =>
      id ? api.put<Funnel>(`/academies/${academyId}/funnels/${id}`, input) : api.post<Funnel>(`/academies/${academyId}/funnels`, input),
    onSuccess: (saved) => {
      // In the list at once, so the page can switch to a new funnel before the refetch.
      qc.setQueryData<Funnel[]>(keys.funnels(academyId), (list) =>
        list ? (list.some((f) => f.id === saved.id) ? list.map((f) => (f.id === saved.id ? saved : f)) : [...list, saved]) : [saved],
      );
      void qc.invalidateQueries({ queryKey: keys.funnels(academyId) });
      void qc.invalidateQueries({ queryKey: ['funnel-board'] });
    },
  });
}

export function useDeleteFunnel(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/academies/${academyId}/funnels/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.funnels(academyId) }),
  });
}

/** Moves a student by hand; both funnels' boards change. */
export function useMoveCard(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ funnelId, enrollmentId, toFunnelId, stageId }: { funnelId: string; enrollmentId: string; toFunnelId: string; stageId: string | null }) =>
      api.put(`/academies/${academyId}/funnels/${funnelId}/cards/${enrollmentId}`, { toFunnelId, stageId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['funnel-board'] }),
  });
}

/** What students of the course see, for the author's lesson preview. */
export function useCourseBanners(courseId: string) {
  return useQuery({ queryKey: keys.courseBanners(courseId), queryFn: () => api.get<CourseBanners>(`/courses/${courseId}/banners`), retry: false });
}

/** Creates a banner, or replaces one when `id` is given. */
export function useSaveBanner(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: BannerInput }) =>
      id ? api.put<Banner>(`/academies/${academyId}/banners/${id}`, input) : api.post<Banner>(`/academies/${academyId}/banners`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.banners(academyId) });
      void qc.invalidateQueries({ queryKey: ['course-banners'] });
      // Banner pictures count against the plan's file space.
      void qc.invalidateQueries({ queryKey: keys.plan });
    },
  });
}

export function useDeleteBanner(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/academies/${academyId}/banners/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.banners(academyId) });
      void qc.invalidateQueries({ queryKey: ['course-banners'] });
      void qc.invalidateQueries({ queryKey: keys.plan });
    },
  });
}

// ---------- courses

export function useCourses(academyId: string | undefined) {
  return useQuery({
    queryKey: keys.courses(academyId ?? ''),
    queryFn: () => api.get<CourseRow[]>(`/academies/${academyId}/courses`),
    enabled: Boolean(academyId),
  });
}

export function useCourse(courseId: string | undefined) {
  return useQuery({
    queryKey: keys.course(courseId ?? ''),
    queryFn: () => api.get<CourseDetail>(`/courses/${courseId}`),
    enabled: Boolean(courseId),
  });
}

export function useCreateCourse(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; description?: string }) => api.post<CourseRow>(`/academies/${academyId}/courses`, payload),
    onSuccess: () => invalidateAcademy(qc, academyId),
  });
}

export function useUpdateCourse(academyId: string, courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { title?: string; description?: string; active?: boolean }) => api.patch<CourseDetail>(`/courses/${courseId}`, patch),
    onSuccess: (course) => {
      qc.setQueryData(keys.course(courseId), course);
      invalidateAcademy(qc, academyId);
    },
  });
}

export function useDeleteCourse(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) => api.delete(`/courses/${courseId}`),
    onSuccess: () => {
      invalidateAcademy(qc, academyId);
      void qc.invalidateQueries({ queryKey: keys.students(academyId) });
      void qc.invalidateQueries({ queryKey: keys.promos(academyId) });
    },
  });
}

/** Mutations that change a course structure share the same refresh. */
function useCourseMutation<TVars, TData>(academyId: string, courseId: string, fn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.course(courseId) });
      invalidateAcademy(qc, academyId);
    },
  });
}

export function useAddSection(academyId: string, courseId: string) {
  return useCourseMutation(academyId, courseId, (title?: string) => api.post<SectionWithLessons>(`/courses/${courseId}/sections`, { title }));
}

export function useRenameSection(academyId: string, courseId: string) {
  return useCourseMutation(academyId, courseId, ({ id, title }: { id: string; title: string }) => api.patch(`/sections/${id}`, { title }));
}

export function useDeleteSection(academyId: string, courseId: string) {
  return useCourseMutation(academyId, courseId, (id: string) => api.delete(`/sections/${id}`));
}

export function useReorder(academyId: string, courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (structure: StructureInput) => api.put<SectionWithLessons[]>(`/courses/${courseId}/structure`, structure),
    onMutate: async (structure) => {
      await qc.cancelQueries({ queryKey: keys.course(courseId) });
      const previous = qc.getQueryData<CourseDetail>(keys.course(courseId));
      if (previous) {
        const lessons = new Map(previous.sections.flatMap((s) => s.lessons).map((l) => [l.id, l]));
        const titles = new Map(previous.sections.map((s) => [s.id, s.title]));
        const sections = structure.sections.map((s) => ({
          id: s.id,
          title: titles.get(s.id) ?? '',
          lessons: s.lessonIds.flatMap((id) => {
            const lesson = lessons.get(id);
            return lesson ? [lesson] : [];
          }),
        }));
        qc.setQueryData<CourseDetail>(keys.course(courseId), { ...previous, sections });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(keys.course(courseId), context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.course(courseId) });
      invalidateAcademy(qc, academyId);
    },
  });
}

// ---------- lessons

export function useLesson(lessonId: string | undefined) {
  return useQuery({
    queryKey: keys.lesson(lessonId ?? ''),
    queryFn: () => api.get<LessonDetail>(`/lessons/${lessonId}`),
    enabled: Boolean(lessonId),
  });
}

export interface LessonPayload {
  title: string;
  blocks: LessonBlock[];
}

export function useSaveLesson(academyId: string, courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lessonId, sectionId, data }: { lessonId: string | null; sectionId: string | null; data: LessonPayload }) =>
      lessonId
        ? api.patch<LessonDetail>(`/lessons/${lessonId}`, data)
        : api.post<LessonDetail>(`/courses/${courseId}/lessons`, { ...data, sectionId }),
    onSuccess: (lesson) => {
      qc.setQueryData(keys.lesson(lesson.id), lesson);
      void qc.invalidateQueries({ queryKey: keys.course(courseId) });
      invalidateAcademy(qc, academyId);
    },
  });
}

export function useDeleteLesson(academyId: string, courseId: string) {
  return useCourseMutation(academyId, courseId, (lessonId: string) => api.delete(`/lessons/${lessonId}`));
}

// ---------- students & invites

/** `enabled` is false for team editors, who do not see students. */
export function useStudents(academyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.students(academyId ?? ''),
    queryFn: () => api.get<EnrollmentRow[]>(`/academies/${academyId}/students`),
    enabled: Boolean(academyId) && enabled,
  });
}

/** Every lesson of one enrollment with when the student viewed and completed it. */
export function useStudentProgress(academyId: string, enrollmentId: string) {
  return useQuery({
    queryKey: keys.studentProgress(enrollmentId),
    queryFn: () => api.get<StudentProgress>(`/academies/${academyId}/students/${encodeURIComponent(enrollmentId)}/progress`),
  });
}

export function useInvite(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, text }: { courseId: string; text: string }) => api.post<InviteResult>(`/courses/${courseId}/invites`, { text }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keys.students(academyId) });
      void qc.invalidateQueries({ queryKey: keys.course(vars.courseId) });
      invalidateAcademy(qc, academyId);
    },
  });
}

// ---------- promo codes

export function usePromos(academyId: string | undefined) {
  return useQuery({
    queryKey: keys.promos(academyId ?? ''),
    queryFn: () => api.get<PromoRow[]>(`/academies/${academyId}/promos`),
    enabled: Boolean(academyId),
  });
}

export function useCreatePromos(academyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromoInput) => api.post<PromoRow[]>(`/academies/${academyId}/promos`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.promos(academyId) }),
  });
}

// ---------- public pages & student area

export function usePublicAcademy(slug: string) {
  return useQuery({ queryKey: keys.publicAcademy(slug), queryFn: () => api.get<PublicAcademy>(`/public/academies/${slug}`), retry: false });
}

export function usePublicCourse(academy: string, course: string) {
  return useQuery({
    queryKey: keys.publicCourse(academy, course),
    queryFn: () => api.get<PublicCourse>(`/public/courses/${academy}/${course}`),
    retry: false,
  });
}

export function useActivate(academy: string, course: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; name: string; promo: string }) =>
      api.post<ActivationResult>(`/public/courses/${academy}/${course}/activate`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.studentCourse(academy, course) });
      void qc.invalidateQueries({ queryKey: keys.publicCourse(academy, course) });
    },
  });
}

export function useStudentLoginLink(academy: string, course: string) {
  return useMutation({
    mutationFn: (email: string) => api.post<{ sent: boolean } & DevLink>(`/public/courses/${academy}/${course}/login-link`, { email }),
  });
}

export function useStudentCourse(academy: string, course: string) {
  return useQuery({
    queryKey: keys.studentCourse(academy, course),
    queryFn: () => api.get<StudentCourse>(`/student/courses/${academy}/${course}`),
    retry: false,
  });
}

export function useStudentLesson(lessonId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.studentLesson(lessonId ?? ''),
    queryFn: () => api.get<StudentLesson>(`/student/lessons/${lessonId}`),
    enabled: Boolean(lessonId) && enabled,
    retry: false,
  });
}

export function useLessonProgress(academy: string, course: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lessonId, done }: { lessonId: string; done: boolean }) => api.put(`/student/lessons/${lessonId}/progress`, { done }),
    onMutate: async ({ lessonId, done }) => {
      const key = keys.studentCourse(academy, course);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<StudentCourse>(key);
      if (previous) {
        qc.setQueryData<StudentCourse>(key, {
          ...previous,
          course: {
            ...previous.course,
            sections: previous.course.sections.map((s) => ({
              ...s,
              lessons: s.lessons.map((l) => (l.id === lessonId ? { ...l, done } : l)),
            })),
          },
        });
      }
      const lessonKey = keys.studentLesson(lessonId);
      const lesson = qc.getQueryData<StudentLesson>(lessonKey);
      if (lesson) qc.setQueryData<StudentLesson>(lessonKey, { ...lesson, done });
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(keys.studentCourse(academy, course), context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.studentCourse(academy, course) }),
  });
}

export function useRenewAccess(academy: string, course: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<{ accessUntil: string | null; code: string }>(`/student/courses/${academy}/${course}/renew`, { code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.studentCourse(academy, course) });
      void qc.invalidateQueries({ queryKey: keys.publicCourse(academy, course) });
    },
  });
}

export function useStudentLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/student/logout'),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['student-course'] });
      qc.removeQueries({ queryKey: ['student-lesson'] });
      void qc.invalidateQueries({ queryKey: ['public-course'] });
    },
  });
}
